import { AxiosResponse } from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { RequestClientInterfaceService } from './request-client.interface.service';
import {
    COGNITO_TOKEN_NAME,
    DEALER_PORTAL_URL,
    PERMISSIONS_TOKEN_NAME,
    USER_NAME,
} from '../config';
import { HttpMethod, HttpStatusCode } from '../utils/enums/http-method.enum';

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class LoginService {
    private readonly dealerPortalUrlStructure = `${DEALER_PORTAL_URL}`;
    private client: RequestClientInterfaceService;
    private headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: '',
    };
    /**
     * Instantiates a new ClientService
     */
    constructor() {
        this.client = new RequestClientInterfaceService();
    }

    /**
     * Retrieves the dealer portal login data
     * @param username Username for user authentication
     * @param password Password for user authentication
     */
    public async dealerPortalLogin(
        username: string,
        password: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/auth`;
        const fingerprint = await this.getFingerprint();

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
            },
            data: { username, password, fingerprint },
            validateStatus: this.validateLockedAndBadRequestStatus,
        };
        return this.client.sendRequest(request);
    }

    private async getFingerprint() {
        const fpPromise = await FingerprintJS.load({ monitoring: false });
        const fp = await fpPromise.get();
        return fp.visitorId;
    }

    /**
     * Send qr 6 digit code
     * @param username Username (email) used for authentication validation
     * @param token 6 digit of generate by authenticator used for authentication validation
     * @param password dealer's account password
     * */
    public async sendAuthCode(
        username: string,
        token: string,
        password: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/verifyToken`;
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
            },
            data: { username, token, password },
            validateStatus: this.validateLockedStatus,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Request to register device for current user
     * @param username Username (email) used to register device
     * */
    public async registerDevice(username: string): Promise<AxiosResponse> {
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        if (cognitoToken && permissionsToken) {
            const url = `${this.dealerPortalUrlStructure}/registerDevice`;
            const fingerprint = await this.getFingerprint();
            const request = {
                method: HttpMethod.post,
                url,
                headers: {
                    ...this.headers,
                    Authorization: `Bearer ${cognitoToken}`,
                },
                data: { username, fingerprint, jwtToken: permissionsToken },
            };
            return this.client.sendRequest(request);
        } else {
            throw new Error('Token request error');
        }
    }

    public async refreshToken(username: string): Promise<AxiosResponse> {
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        if (cognitoToken && permissionsToken) {
            const url = `${this.dealerPortalUrlStructure}/tokenRefresher`;
            const request = {
                method: HttpMethod.post,
                url,
                headers: {
                    ...this.headers,
                    Authorization: `Bearer ${cognitoToken}`,
                },
                data: { username, jwtToken: permissionsToken },
            };
            return this.client.sendRequest(request);
        } else {
            throw new Error('Token request error');
        }
    }

    public async getDealerProfile(token: string): Promise<AxiosResponse> {
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const username = window.sessionStorage.getItem(USER_NAME);

        if (cognitoToken && username && token) {
            const url = `${this.dealerPortalUrlStructure}/getDealerProfile`;
            const request = {
                method: HttpMethod.post,
                url,
                headers: {
                    ...this.headers,
                    Authorization: `Bearer ${cognitoToken}`,
                },
                data: { username, jwtToken: token },
            };
            return this.client.sendRequest(request);
        } else {
            throw new Error('Token request error');
        }
    }

    /**
     * Request to account registration for dealer portal
     * @param codedUser CodeUser retrieved from url query used to register dealer
     * @param password Password used to register dealer
     * @param answer Answer used to register dealer
     * @param securityQuestion Security Question used to register dealer
     * */
    public async accountRegistration(
        codedUser: string,
        password: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/registerDealerUser`;
        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
            },
            data: { codedUser, password },
        };
        return this.client.sendRequest(request);
    }

    validateLockedStatus(status: number) {
        return (
            status == HttpStatusCode.LOCKED ||
            (status >= HttpStatusCode.SUCCESS &&
                status < HttpStatusCode.MULTIPLE_CHOICES)
        );
    }

    validateLockedAndBadRequestStatus(status: number) {
        return (
            status == HttpStatusCode.LOCKED ||
            (status >= HttpStatusCode.SUCCESS &&
                status < HttpStatusCode.MULTIPLE_CHOICES) ||
            status == HttpStatusCode.BAD_REQUEST
        );
    }
}
